import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useCollectionOnce } from "react-firebase-hooks/firestore";
import {
  collection,
  query,
  where,
  orderBy,
  updateDoc,
  doc,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../../services/firebase";
import InventoryDataService from "../../services/inventories.service";

import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import { TextField } from "formik-mui";
import { array, number, object, string } from "yup";
import { CustomizedSelect } from "../../common/select";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import BackspaceTwoToneIcon from "@mui/icons-material/BackspaceTwoTone";
import SaveIcon from "@mui/icons-material/Save";
import Typography from "@mui/material/Typography";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";

import { enqueueSnackbar } from "notistack";

import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";

export const SurveyTruck = () => {
  const [truckId, setTruck] = useState("");
  const navigate = useNavigate();

  const [trucks, loading, error] = useCollectionOnce(
    query(
      collection(db, "trucks-p"),
      where("active", "==", true),
      orderBy("truckNumber")
    )
  );

  const selectTruck = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setTruck(event.target.value);
  };

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="stretch"
      spacing={1}
    >
      <Box display="flex" justifyContent="space-between" p={2}>
        <Typography variant="h5">Truck Reporting</Typography>

        <Box display="flex">
          <Button
            variant="text"
            onClick={() => navigate("/survey")}
            startIcon={<BackspaceTwoToneIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>
      {error && <strong>Error: {JSON.stringify(error)}</strong>}
      {loading && <CircularProgress></CircularProgress>}
      {trucks && (
        <FormControl fullWidth>
          <InputLabel
            id="truck-select-label"
            style={{ backgroundColor: "white" }}
          >
            Select Truck
          </InputLabel>
          <Select
            labelId="truck-select-label"
            id="truck-select"
            label="Location"
            value={truckId}
            onChange={(event) => {
              selectTruck(event);
            }}
          >
            {trucks.docs.map((doc) => (
              <MenuItem
                key={doc.id}
                value={doc.id}
                name={doc.data().truckDescription}
              >
                {doc.data().truckDescription}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {truckId && <SurveyTruckForm truckId={truckId} trucks={trucks} />}
    </Stack>
  );
};

const SurveyTruckForm = (props) => {
  const [reportingDate, setReportingDate] = useState(dayjs());
  const formikRef = useRef();
  const navigate = useNavigate();
  const [products, loading, error] = useCollectionOnce(
    query(
      collection(db, "products-p"),
      where("active", "==", true),
      orderBy("name")
    )
  );

  const [locations, loadingLocations, errorLocations] = useCollectionOnce(
    query(
      collection(db, "locations-p"),
      where("active", "==", true),
      orderBy("name")
    )
  );

  useEffect(() => {
    if (formikRef.current) {
      const truck = props.trucks.docs.find((t) => t.id === props.truckId);
      formikRef.current.setFieldValue("compartments", [
        {
          compartment: 1,
          product: "JbuEzvWFVxRFyDG9BRUD",
          percent: truck.data().latestReadingPercent ?? 0,
        },
      ]);
    }
  }, [props.truckId]);

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={object({
        location: string().required("Please select a location"),
        compartments: array(
          object({
            product: string(),
            percent: number()
              .min(0, "Please enter a percent between 0 and 100")
              .max(100, "Please enter a percent between 0 and 100")
              .integer("Please enter percentage in whole numbers"),
          })
        )
          .test({
            name: "percent-for-product",
            message: "You must have greater than zero percent for a product",
            test: (val) =>
              val.every(({ product, percent }, index) => {
                if (product !== undefined && percent <= 0) {
                  //console.log(index, product, percent);
                  return false;
                }

                return true;
              }),
          })
          .test({
            name: "product-for-percent",
            message: "You must have a product when entering percent",
            test: (val) =>
              val.every(({ product, percent }, index) => {
                if (product === undefined && percent > 0) {
                  //console.log(index, product, percent);
                  return false;
                }

                return true;
              }),
          }),
      })}
      initialValues={{
        location: "",
        compartments: [
          {
            compartment: 1,
            product: "JbuEzvWFVxRFyDG9BRUD",
            percent: 0,
          },
        ],
      }}
      onSubmit={async (values) => {
        const entryDate = reportingDate.startOf("day").toDate();
        const lastUpdated = serverTimestamp();

        try {
          const truck = props.trucks.docs.find((t) => t.id === props.truckId);
          console.log("truck", truck.data());

          for (const compartment of values.compartments) {
            const gallons =
              truck.data().truckCapacity * (compartment.percent / 10);

            if (compartment.percent > 0) {
              const data = {
                reportingDate: entryDate,
                locationId: values.location,
                truckId: props.truckId,
                compartment: compartment.compartment,
                productId: compartment.product,
                gallons: gallons,
                percentOfCapacity: compartment.percent,
                capacity: truck.data().truckCapacity,
                lastUpdatedBy: auth.currentUser.uid,
              };
              console.log(data);
              await InventoryDataService.create(data);

              if (
                !truck.lastReading ||
                truck.lastReading <= Timestamp.fromDate(entryDate)
              ) {
                await updateDoc(doc(db, "trucks-p", truck.id), {
                  lastReading: Timestamp.fromDate(entryDate),
                  latestReadingPercent: compartment.percent,
                });
              }
            }
          }

          enqueueSnackbar("Truck Inventory Recorded");
          navigate("/survey");
        } catch (error) {
          enqueueSnackbar(error);
        }
      }}
    >
      {({ values, errors, isSubmitting }) => (
        <Form>
          <Stack spacing={0.5}>
            <Typography variant="h5" textAlign={"center"}>
              Enter Truck Inventory Information
            </Typography>
            <Typography variant="subtitle1" textAlign={"center"}>
              Please enter inventory by the percent of volume. The value 20 will
              be taken as 20%.
            </Typography>
            {errorLocations && (
              <Grid item xs={12}>
                <strong>Error: {JSON.stringify(errorLocations)}</strong>
              </Grid>
            )}
            {loadingLocations && (
              <Grid item xs={12}>
                <Skeleton />
              </Grid>
            )}
            {locations && (
              <Grid container spacing={1}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="h6">
                    Where did you load your truck?
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Field
                    fullWidth
                    component={CustomizedSelect}
                    label="Select Location"
                    name={`location`}
                  >
                    <MenuItem value="" name="Location Not Selected" selected>
                      Location Not Selected
                    </MenuItem>
                    {locations.docs.map((doc) => (
                      <MenuItem
                        key={doc.id}
                        value={doc.id}
                        name={doc.data().name}
                      >
                        {doc.data().name}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box display="flex" justifyContent="right">
                    <ErrorMessage
                      name="location"
                      component="div"
                      className="cehm-error"
                    ></ErrorMessage>
                  </Box>
                </Grid>
              </Grid>
            )}
            <FieldArray name="compartments">
              {() => (
                <>
                  {values.compartments.map((comp, index) => (
                    <Grid container key={index} spacing={1}>
                      {error && (
                        <Grid item xs={12}>
                          <strong>Error: {JSON.stringify(error)}</strong>
                        </Grid>
                      )}
                      {loading && (
                        <Grid item xs={12}>
                          <Skeleton />
                        </Grid>
                      )}
                      {products && (
                        <>
                          <Grid
                            item
                            xs={12}
                            sm
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Typography variant="h6">
                              Compartment {comp.compartment}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm>
                            <Field
                              fullWidth
                              component={CustomizedSelect}
                              products={products}
                              label="Select Product"
                              name={`compartments[${index}].product`}
                            >
                              {products.docs.map((doc) => (
                                <MenuItem
                                  key={doc.id}
                                  value={doc.id}
                                  name={doc.data().name}
                                  selected={true}
                                >
                                  {doc.data().name}
                                </MenuItem>
                              ))}
                            </Field>
                          </Grid>
                          <Grid item xs={6} sm>
                            <Field
                              fullWidth
                              name={`compartments[${index}].percent`}
                              component={TextField}
                              label="Percent Full"
                              type="number"
                              variant="outlined"
                            ></Field>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  ))}
                </>
              )}
            </FieldArray>
            <Box display="flex" justifyContent="right">
              <ErrorMessage
                name="compartments"
                component="div"
                className="cehm-error"
              ></ErrorMessage>
            </Box>
            <Box display="flex" justifyContent="right" p={2}>
              <Box display="flex" pr={2}>
                <DatePicker
                  name="reportingDate"
                  label="Reporting Date"
                  value={reportingDate}
                  onChange={(newValue) => setReportingDate(newValue)}
                ></DatePicker>
              </Box>
              <Box display="flex">
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size="0.9rem" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                >
                  {isSubmitting ? "Submitting" : "Submit"}
                </Button>
              </Box>
            </Box>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
